import { useEffect, useState } from 'react';
import { TrendingUp, TriangleAlert, Minus, MessageSquare } from 'lucide-react';

import { getFeedbackSummary } from '../../../services/StudentFeedbackService';
import {
  bandFor,
  BAND_COPY,
  MIN_RESPONSES,
  ratingSegments,
} from './feedbackBands';
import BandDonut from './BandDonut';
import { Skeleton, SkeletonGroup } from '../common/Skeleton/Skeleton.jsx';
import { silenceFor } from '../common/Loader/busy';
/* The .ba box and .ba-canvas frame this borrows live with the attendance
   report. Imported here rather than relied on: the trainer dashboard does
   not load that stylesheet itself, so on a trainer's own route the box
   came up unframed unless an admin page had been opened first. */
import '../Dashboard/BusinessPulse.css';
import '../Dashboard/trainer-feedback-report.css';
import './my-feedback-summary.css';

/**
 * A trainer's own feedback, and whether it is a problem.
 *
 * The same verdict the administrator's dashboard reaches, from the same
 * thresholds in feedbackBands.js — so a trainer reading this is reading
 * what their head of department reads, not a softened version of it.
 * Being told you are fine by your own dashboard and at risk by someone
 * else's is worse than not being told at all.
 *
 * No `trainerId`: /summary is scoped by the server to the caller's own
 * sessions and batches. Nothing here can ask about anybody else.
 */

const BAND_ICON = {
  good: TrendingUp,
  risk: TriangleAlert,
  steady: Minus,
  quiet: MessageSquare,
};

const MyFeedbackSummary = ({ onSeeAll }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // The box shows its own skeleton, so the global overlay stays down.
    silenceFor();
    getFeedbackSummary()
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const total = Number(summary?.total) || 0;
  const average = Number(summary?.averageRating) || 0;
  const lowCount = Number(summary?.lowRatingCount) || 0;

  const band = bandFor({ averageRating: average, lowCount, responses: total });
  const copy = BAND_COPY[band];
  const BandIcon = BAND_ICON[band];

  return (
    <section className="ba tfr mfs">
      <header className="ba-head">
        <div>
          <h2 className="ba-title">What your students said</h2>
          <p className="ba-sub">
            {loading
              ? 'Reading your feedback…'
              : total === 0
                ? 'No student has rated one of your sessions yet.'
                : `${total} ${total === 1 ? 'response' : 'responses'} across your sessions`}
          </p>
        </div>

        {onSeeAll && total > 0 && (
          <button type="button" className="td-head-action" onClick={onSeeAll}>
            Show more
          </button>
        )}
      </header>

      {loading ? (
        /* The verdict and the figures, shimmering, until the summary
           arrives -- zeros meanwhile read as "no feedback". */
        <SkeletonGroup label="Loading your feedback" className="mfs-body">
          <div className="lms-skel-row">
            <Skeleton shape="mark" />
            <div className="lms-skel-stack lms-skel-grow">
              <Skeleton width="25" />
              <Skeleton width="70" />
            </div>
          </div>
          <div className="mfs-figures">
            <div className="lms-skel-stack">
              <Skeleton shape="figure" width="40" />
              <Skeleton width="55" />
            </div>
            <Skeleton shape="ring" />
          </div>
        </SkeletonGroup>
      ) : total === 0 ? (
        <p className="tfr-empty">
          Once a class is marked, its students are asked to rate it. Their
          answers land here.
        </p>
      ) : (
        <div className="mfs-body">
          {/* The verdict first, in a word, because it is the only part of
              this box that asks for a decision. */}
          <div className={`mfs-verdict mfs-verdict--${band}`}>
            <span className="mfs-verdict-mark" aria-hidden="true">
              <BandIcon size={18} />
            </span>
            <span className="mfs-verdict-id">
              <span className="mfs-verdict-label">{copy.label}</span>
              <span className="mfs-verdict-line">{copy.line}</span>
            </span>
          </div>

          {/* The working, beside the verdict rather than under it: on a
              desktop the box is wider than either half needs, and a
              chart run to the full width of it reads as the headline
              when it is the evidence. */}
          <div className="mfs-figures">
            <div className="mfs-score">
              <b>{average ? average.toFixed(1) : '—'}</b>
              <span>out of 5</span>
              {lowCount > 0 && (
                <small className="mfs-low">{lowCount} rated 2 or below</small>
              )}
            </div>

            <div className="mfs-chart">
              <h3 className="mfs-chart-head">How the ratings split</h3>
              <BandDonut
                segments={ratingSegments(summary?.ratingGroups)}
                centreValue={total}
                centreLabel={total === 1 ? 'response' : 'responses'}
                empty="Nobody has rated a session yet."
              />
            </div>
          </div>

          {band === 'quiet' && total > 0 && (
            <p className="tfr-empty mfs-note">
              {total} of {MIN_RESPONSES} responses needed before this counts as
              a verdict either way.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default MyFeedbackSummary;
